import React from "react";
import moment from "moment";
import { Box, Button, CircularProgress, Tooltip, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { JoinRequestChip } from "../commonComponents/JoinRequestStatus";

// a long rejection reason is cut short on the line (the ⓘ shows it in full)
const REASON_LENGTH = 40;

// One line in the auction header for a signed-in bidder who is not a
// participant: the invitation to request to participate, or where his
// request stands. The longer explanation is behind the ⓘ.
const JoinRequestBox = ({ details, onRequest, onWithdraw, withdrawing }) => {
  const request = details?.joinRequest;
  const organiser = details?.auctioneerName || "the organiser";
  if (!details?.joinRequestsOpen && !request) return null;

  const info = (text) => (
    <Tooltip title={text} enterTouchDelay={0}>
      <InfoOutlinedIcon
        tabIndex={0}
        aria-label={text}
        sx={{ fontSize: 18, color: "#4B5563", cursor: "help" }}
      />
    </Tooltip>
  );

  const line = (children) => (
    <Box
      sx={{
        mt: 1.5,
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        columnGap: 1.5,
        rowGap: 0.5,
      }}
    >
      {children}
    </Box>
  );

  const text = (words) => (
    <Typography component="span" sx={{ fontSize: 14, color: "#374151" }}>
      {words}
    </Typography>
  );

  const requestButton = (label) => (
    <Button
      variant="contained"
      size="small"
      onClick={onRequest}
      sx={{ backgroundColor: "#2D55EB", borderRadius: 0 }}
    >
      {label}
    </Button>
  );

  const explanation = `Send your company details to ${organiser}. If they approve you, they choose the lots you can bid on, and you accept the terms as usual.`;

  if (!request) {
    return line(
      <>
        {text("You're not a participant yet")}
        {info(explanation)}
        {details.canRequest && requestButton("Request to participate")}
      </>
    );
  }

  const sentOn = moment(request.createdAt).format("D MMM");

  switch (request.status) {
    case "PENDING":
      return line(
        <>
          <JoinRequestChip state="WAITING" />
          {text(`Request sent ${sentOn}`)}
          {info(`${organiser} will review it. You'll see their answer here and on My requests.`)}
          <Button
            size="small"
            onClick={onWithdraw}
            disabled={withdrawing}
            startIcon={withdrawing ? <CircularProgress size={14} /> : null}
          >
            Withdraw
          </Button>
        </>
      );
    case "REJECTED": {
      const reason = request.rejectionReason || "";
      const short =
        reason.length > REASON_LENGTH ? `${reason.slice(0, REASON_LENGTH)}…` : reason;
      return line(
        <>
          <JoinRequestChip state="NOT_ACCEPTED" />
          {text(short ? `"${short}"` : `${organiser} did not accept your request`)}
          {info(
            [
              `${organiser} did not accept your request.`,
              reason && `Reason: "${reason}"`,
              !request.canRequestAgain && "You can't send another request for this auction.",
            ]
              .filter(Boolean)
              .join(" ")
          )}
          {request.canRequestAgain && requestButton("Request again")}
        </>
      );
    }
    case "WITHDRAWN":
      return line(
        <>
          <JoinRequestChip state="WITHDRAWN" />
          {text("You withdrew your request")}
          {details.canRequest && requestButton("Request to participate")}
        </>
      );
    case "CLOSED":
      return request.closedReason === "AUCTION_ENDED"
        ? line(
            <>
              <JoinRequestChip state="CLOSED" />
              {text("Auction ended")}
              {info(`The auction ended before ${organiser} answered your request.`)}
            </>
          )
        : line(
            <>
              <JoinRequestChip state="REMOVED" />
              {text("You're no longer a participant")}
              {info(`${organiser} removed you from this auction. You can't send another request.`)}
            </>
          );
    case "APPROVED":
      // approved, but no longer a participant
      return line(
        <>
          <JoinRequestChip state="REMOVED" />
          {text("You're no longer a participant")}
          {info(`${organiser} removed you from this auction. You can't send another request.`)}
        </>
      );
    default:
      return null;
  }
};

export default JoinRequestBox;
